/**
 * Locale-aware formatting. Use these instead of toLocaleString()/hand-rolled
 * formatters so dates and numbers follow the active language.
 */

import { i18n } from "./i18n";

const locale = () => i18n.language || "en";

export type DateInput = Date | string | number;
const DEFAULT_DATE_OPTIONS: Intl.DateTimeFormatOptions = { dateStyle: "medium" };

export function formatDate(value: DateInput, options: Intl.DateTimeFormatOptions = DEFAULT_DATE_OPTIONS) {
  return new Intl.DateTimeFormat(locale(), options).format(new Date(value));
}

export function formatDateTime(value: DateInput) {
  return formatDate(value, { dateStyle: "medium", timeStyle: "short" });
}

export function formatNumber(value: number, options?: Intl.NumberFormatOptions) {
  return new Intl.NumberFormat(locale(), options).format(value);
}

export function formatRelativeTime(value: DateInput, now: Date = new Date()) {
  const seconds = Math.round((new Date(value).getTime() - now.getTime()) / 1000);
  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ["year", 31_536_000],
    ["month", 2_592_000],
    ["week", 604_800],
    ["day", 86_400],
    ["hour", 3_600],
    ["minute", 60],
  ];
  const rtf = new Intl.RelativeTimeFormat(locale(), { numeric: "auto" });
  for (const [unit, size] of units) {
    if (Math.abs(seconds) >= size) return rtf.format(Math.round(seconds / size), unit);
  }
  return rtf.format(seconds, "second");
}

/** "A, B" (unit style: no "and"), in the active language's list punctuation. */
export function formatList(items: string[]) {
  return new Intl.ListFormat(locale(), { type: "unit" }).format(items);
}

export const formatUsd = (value: number) => formatNumber(value, { style: "currency", currency: "USD" });

export const formatCompact = (value: number) => formatNumber(value, { notation: "compact", maximumFractionDigits: 1 });

type DurationUnit = "hour" | "minute" | "second";

/** "1h 2m" / "3m 5s" / "5s": the two largest units, in the active language. */
export function formatDuration(seconds: number) {
  const total = Math.max(0, Math.round(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  let parts: Array<[number, DurationUnit]> = [[s, "second"]];
  if (h > 0) {
    parts = [
      [h, "hour"],
      [m, "minute"],
    ];
  } else if (m > 0) {
    parts = [
      [m, "minute"],
      [s, "second"],
    ];
  }
  return new Intl.ListFormat(locale(), { type: "unit", style: "narrow" }).format(
    parts.map(([value, unit]) => formatNumber(value, { style: "unit", unit, unitDisplay: "narrow" })),
  );
}

const BYTE_UNITS = ["byte", "kilobyte", "megabyte", "gigabyte"] as const;

/**
 * "76 MB" / "1.5 kB" / "512 bytes" in the active language. Binary steps (1 MB = 1024 × 1024 bytes), matching how the
 * backend defines its limits, so a 50 MB limit reads as "50 MB".
 */
export function formatBytes(bytes: number) {
  let value = Math.max(0, bytes);
  let unit = 0;
  while (value >= 1024 && unit < BYTE_UNITS.length - 1) {
    value /= 1024;
    unit++;
  }
  return formatNumber(value, {
    style: "unit",
    unit: BYTE_UNITS[unit],
    unitDisplay: unit === 0 ? "long" : "short",
    maximumFractionDigits: unit === 0 || value >= 10 ? 0 : 1,
  });
}
